import type { InfoPanelLayoutPreferences } from './user-preferences';

/**
 * Organization Page Types
 * Custom pages that appear as tabs in the "My Organization" section.
 */

/**
 * Cover type for org pages
 */
export type OrgPageCoverType = 'photo' | 'gradient' | 'color';

/**
 * Preset gradient options for org page covers
 * Each gradient has a unique key and CSS gradient value
 */
export interface GradientPreset {
  id: string;
  name: string;
  value: string; // CSS gradient value
  category: 'modern' | 'warm' | 'cool' | 'vibrant' | 'subtle';
}

/**
 * Curated gradient presets for organization page covers
 */
export const GRADIENT_PRESETS: GradientPreset[] = [
  // Modern
  { id: 'midnight-city', name: 'Midnight City', value: 'linear-gradient(135deg, #232526 0%, #414345 100%)', category: 'modern' },
  { id: 'steel-gray', name: 'Steel Gray', value: 'linear-gradient(135deg, #1f1c2c 0%, #928dab 100%)', category: 'modern' },
  { id: 'deep-space', name: 'Deep Space', value: 'linear-gradient(135deg, #000000 0%, #434343 100%)', category: 'modern' },

  // Warm
  { id: 'sunset-glow', name: 'Sunset Glow', value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', category: 'warm' },
  { id: 'warm-flame', name: 'Warm Flame', value: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', category: 'warm' },
  { id: 'peach-blossom', name: 'Peach Blossom', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', category: 'warm' },
  { id: 'coral-reef', name: 'Coral Reef', value: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)', category: 'warm' },

  // Cool
  { id: 'ocean-blue', name: 'Ocean Blue', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', category: 'cool' },
  { id: 'arctic-sky', name: 'Arctic Sky', value: 'linear-gradient(135deg, #e0eafc 0%, #cfdef3 100%)', category: 'cool' },
  { id: 'deep-ocean', name: 'Deep Ocean', value: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)', category: 'cool' },
  { id: 'northern-lights', name: 'Northern Lights', value: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)', category: 'cool' },

  // Vibrant
  { id: 'electric-violet', name: 'Electric Violet', value: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)', category: 'vibrant' },
  { id: 'cyber-grape', name: 'Cyber Grape', value: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', category: 'vibrant' },
  { id: 'neon-life', name: 'Neon Life', value: 'linear-gradient(135deg, #b3ffab 0%, #12fff7 100%)', category: 'vibrant' },
  { id: 'rainbow-blue', name: 'Rainbow Blue', value: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)', category: 'vibrant' },

  // Subtle
  { id: 'soft-metal', name: 'Soft Metal', value: 'linear-gradient(135deg, #d5d4d0 0%, #eeeeec 100%)', category: 'subtle' },
  { id: 'morning-mist', name: 'Morning Mist', value: 'linear-gradient(135deg, #d9afd9 0%, #97d9e1 100%)', category: 'subtle' },
  { id: 'soft-grass', name: 'Soft Grass', value: 'linear-gradient(135deg, #c1dfc4 0%, #deecdd 100%)', category: 'subtle' },
  { id: 'winter-neva', name: 'Winter Neva', value: 'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)', category: 'subtle' },
];

/**
 * Default gradient for new organizations
 */
export const DEFAULT_GRADIENT_ID = 'ocean-blue';

/**
 * Get gradient preset by ID
 */
export function getGradientPreset(id: string): GradientPreset | undefined {
  return GRADIENT_PRESETS.find(g => g.id === id);
}

/**
 * Get default gradient preset
 */
export function getDefaultGradient(): GradientPreset {
  const preset = GRADIENT_PRESETS.find(g => g.id === DEFAULT_GRADIENT_ID);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- GRADIENT_PRESETS always has at least one element
  return preset ?? GRADIENT_PRESETS[0]!;
}

export interface OrgPage {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  order: number;
  isDefault: boolean;
  isActive: boolean;

  // Cover configuration
  coverType: OrgPageCoverType; // 'photo' | 'gradient' | 'color'
  coverPhoto?: string; // S3 key for photo
  coverPhotoUrl?: string; // Signed URL for display
  coverPosition?: string; // CSS background-position for the photo (e.g., '50% 30%')
  coverGradient?: string; // Gradient preset ID (e.g., 'ocean-blue')
  coverColor?: string; // Hex color (e.g., '#4776e6')

  showHeader: boolean;
  layout: InfoPanelLayoutPreferences;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateOrgPageDTO {
  name: string;
  slug?: string; // Auto-generated from name if not provided
  icon?: string;
  description?: string;
  showHeader?: boolean;
  coverType?: OrgPageCoverType;
  coverGradient?: string;
  coverColor?: string;
}

export interface UpdateOrgPageDTO {
  name?: string;
  slug?: string;
  icon?: string;
  description?: string;
  order?: number;
  isDefault?: boolean;
  isActive?: boolean;
  coverType?: OrgPageCoverType;
  coverPhoto?: string;
  coverPosition?: string | null;
  coverGradient?: string;
  coverColor?: string;
  showHeader?: boolean;
  layout?: InfoPanelLayoutPreferences;
}

export interface ReorderOrgPagesDTO {
  pageIds: string[];
}

/**
 * Organization branding types
 */
export interface OrgBranding {
  coverPhoto?: string;
  coverPhotoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  logo?: string;
  logoUrl?: string;
}

export interface UpdateOrgBrandingDTO {
  primaryColor?: string;
  accentColor?: string;
}

/**
 * Default layout for new org pages
 */
export function getDefaultOrgPageLayout(): InfoPanelLayoutPreferences {
  return {
    version: 1,
    sections: [
      {
        id: crypto.randomUUID ? crypto.randomUUID() : `section-${Date.now()}`,
        title: 'Content',
        layout: 'grid',
        gridColumns: 3,
        width: 'full',
        items: [],
      },
    ],
    showUnassignedFields: false,
  };
}

/**
 * Generate slug from page name
 */
export function generatePageSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}
